import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import Spinner from '@components/ui/Spinner'
import { isAllItemsInStock, isPrimaryNB } from './helper'
import {
  EmptyGuid,
  Messages,
  NEXT_BASKET_VALIDATE,
} from '@components/utils/constants'
import { matchStrings, priceFormat } from '@framework/utils/parse-util'
import {
  LocalStorage,
  PaymentMethodType,
} from '@components/utils/payment-constants'
import {
  extractStoreIdFromString,
  getCurrentPage,
  isUpiPSP,
  replaceAll,
} from '@framework/utils/app-util'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { recordGA4Event } from '@components/services/analytics/ga4'
import { useUI } from '@components/ui'
import axios from '@services/axiosInterceptorInstance'
import { useMoEngageEvent } from 'hooks/useMoengageEvent'
import { setItem } from '@components/utils/localStorage'
import { beginBasketExpressCheckout } from './eventsHelper'
import {
  MOBILE_UPI_OPTIONS,
  NETBANKING_OPTIONS,
  WALLET_OPTIONS,
} from '@components/checkout/helper'

interface IProps {
  defaultShippingAddress: any
  reValidateData: any
  isChannelStore: boolean
  setBasketReValidate: any
  preferredPaymentMethod: any
  showBasketRevalidateError: any
  savedCard: any
  initiateExpressCheckout: any
}

const BasketExpressCheckoutMainSection = (props: IProps) => {
  const {
    defaultShippingAddress,
    reValidateData,
    isChannelStore,
    setBasketReValidate,
    preferredPaymentMethod,
    showBasketRevalidateError,
    savedCard,
    initiateExpressCheckout,
  } = props

  const { user, setAlert, storeBasketId, basketId, cartItems, paymentMethods } =
    useUI()
  const router = useRouter()
  const storeId =
    router?.query?.storeId || extractStoreIdFromString(router?.asPath)
  const trackMoEngageEvent = useMoEngageEvent()

  const changeAddress = (address: any) => {
    recordGA4Event(window, GA_EVENT.ADDRESS_CHANGE, {
      delivery_address_name: address?.label,
      current_page: getCurrentPage(),
      loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
    })

    if (isChannelStore) {
      router.push({
        pathname: '/checkout',
        query: {
          channelStore: true,
          storeId: storeId,
        },
      })
    } else {
      router.push('/checkout')
    }
  }

  const fetchBasketReValidate = async () => {
    const { data: reValidate }: any = await axios.post(NEXT_BASKET_VALIDATE, {
      basketId: isChannelStore ? storeBasketId : basketId,
    })
    setBasketReValidate(reValidate?.result)
    return reValidate?.result
  }

  const sendUserInfoToMoengage = (userInfo: any) => {
    if (window.Moengage) {
      const mobileNumber = userInfo?.phoneNo || userInfo?.mobileNo
      window.Moengage.update_unique_user_id(mobileNumber)
      window.Moengage.add_unique_user_id(mobileNumber)
      window.Moengage.add_mobile(mobileNumber)

      if (!user?.['firstName'] && !user?.['lastName']) {
        window.Moengage.add_first_name(userInfo.firstName)
        window.Moengage.add_last_name(userInfo.lastName)
        window.Moengage.add_user_name(
          `${userInfo.firstName} ${userInfo.lastName}`
        )
      }
    }
  }

  return (
    <div className="p-4 pt-6 bg-white drop-shadow-lg">
      <div className="flex justify-between py-2">
        {!defaultShippingAddress ? (
          <Spinner />
        ) : (
          <>
            {defaultShippingAddress?.id ? (
              <>
                <div className="">
                  <h4 className="font-10 text-brown-light">
                    Delivery at{' '}
                    <span className="font-semibold">
                      {defaultShippingAddress?.label}
                    </span>
                  </h4>
                  <p className="text-xs truncate text-primary max-width-250">
                    {`${defaultShippingAddress?.firstName} ${defaultShippingAddress?.lastName}`.trim()}
                    , {defaultShippingAddress?.defaultShippingAddress},{' '}
                    {defaultShippingAddress?.address2},{' '}
                    {defaultShippingAddress?.city},{' '}
                    {defaultShippingAddress?.state},{' '}
                    {defaultShippingAddress?.country}
                  </p>
                </div>
                <p>
                  <a
                    // href="#"
                    className="font-semibold text-orange font-14 cursor-pointer"
                    onClick={(ev: any) => changeAddress(defaultShippingAddress)}
                  >
                    Change
                  </a>
                </p>
              </>
            ) : (
              <div className="">No address found</div>
            )}
          </>
        )}
      </div>
      <div className="flex justify-between chk-btn">
        <a
          href="javascript:void(0);"
          className="flex flex-col content-center justify-center w-2/6 px-2 py-3 border border-gray-200 align-center"
          onClick={async (ev: any) => {
            if (defaultShippingAddress?.id) {
              // Validate basket
              const reValidateResult = await fetchBasketReValidate()
              if (isAllItemsInStock(reValidateResult)) {
                const messageLength: number =
                  reValidateData?.message?.split(',').length ?? 0

                if (messageLength == 0) {
                  // No error while basket validation
                  sendUserInfoToMoengage(defaultShippingAddress)
                  if (isChannelStore) {
                    router.push({
                      pathname: `/checkout/payment/${defaultShippingAddress?.id}`,
                      query: {
                        channelStore: true,
                        storeId: storeId,
                      },
                    })
                  } else {
                    router.push(
                      `/checkout/payment/${defaultShippingAddress?.id}`
                    )
                  }
                  beginBasketExpressCheckout(
                    cartItems,
                    user,
                    preferredPaymentMethod,
                    defaultShippingAddress,
                    isChannelStore,
                    trackMoEngageEvent
                  )
                } else {
                  if (showBasketRevalidateError) {
                    setAlert({
                      type: 'error',
                      msg: Messages.Errors['BASKET_VALIDATION_FAILED'],
                    })
                  }
                }
              }
            }
          }}
        >
          <p className="flex items-center mb-1 text-xs leading-none text-brown-light">
            Pay Using
            <span className="px-2 leading-none rotate-180 opacity-50 line-height-0">
              <i className="align-middle sprite-icon sprite-down"></i>
            </span>
          </p>

          {!preferredPaymentMethod ? (
            <Spinner />
          ) : (
            <>
              {/* cod */}
              {preferredPaymentMethod?.method &&
                matchStrings(
                  preferredPaymentMethod?.method,
                  PaymentMethodType.COD,
                  true
                ) && <p className="text-xs dark:text-black">Pay on delivery</p>}

              {/* cards */}
              {preferredPaymentMethod?.method &&
                matchStrings(
                  preferredPaymentMethod?.method,
                  PaymentMethodType.CARD,
                  true
                ) && (
                  <p className="text-xs dark:text-black">
                    <i className="sprite-icon sprite-master"></i>{' '}
                    {savedCard?.card_number}
                  </p>
                )}

              {/* wallet */}
              {preferredPaymentMethod?.method &&
                matchStrings(
                  preferredPaymentMethod?.method,
                  PaymentMethodType.WALLET,
                  true
                ) &&
                preferredPaymentMethod?.type && (
                  <p className="text-xs dark:text-black">
                    <i
                      className={`sprite-icon ${
                        WALLET_OPTIONS?.find((x: any) =>
                          matchStrings(
                            x?.key,
                            replaceAll(
                              preferredPaymentMethod?.type,
                              '',
                              Messages.Validations.RegularExpressions
                                .FIND_EMPTY_CHARACTERS
                            ),
                            true
                          )
                        )?.className
                      }`}
                    ></i>{' '}
                    Wallet
                  </p>
                )}

              {/* upis */}
              {preferredPaymentMethod?.method &&
                matchStrings(
                  preferredPaymentMethod?.method,
                  PaymentMethodType.UPI,
                  true
                ) && (
                  <>
                    <p className="text-xs dark:text-black">
                      <span className="inline-block pb-1">
                        <i
                          className={`align-middle sprite-icon ${
                            MOBILE_UPI_OPTIONS?.find((x: any) =>
                              matchStrings(
                                x?.key,
                                replaceAll(
                                  preferredPaymentMethod?.type,
                                  '',
                                  Messages.Validations.RegularExpressions
                                    .FIND_EMPTY_CHARACTERS
                                ),
                                true
                              )
                            )?.className
                          }`}
                        ></i>{' '}
                        UPI
                      </span>

                      {!isUpiPSP(preferredPaymentMethod?.key) && (
                        <>
                          {' '}
                          <span
                            className="block text-xs truncate max-width-upi"
                            title={preferredPaymentMethod?.key}
                          >
                            {preferredPaymentMethod?.key}
                          </span>
                        </>
                      )}
                    </p>
                  </>
                )}

              {/* nb */}
              {preferredPaymentMethod?.method &&
                matchStrings(
                  preferredPaymentMethod?.method,
                  PaymentMethodType.NETBANKING,
                  true
                ) && (
                  <>
                    {isPrimaryNB(preferredPaymentMethod) ? (
                      <>
                        {NETBANKING_OPTIONS?.filter((x: any) =>
                          matchStrings(
                            x?.key,
                            `${preferredPaymentMethod?.key}_${preferredPaymentMethod?.type}`
                          )
                        )?.map((option: any, idx: number) => (
                          <div
                            key={idx}
                            className="relative w-full py-2 pl-12 pr-2"
                          >
                            <span className="absolute leading-none sm:left-0 left-4 top-2/4 -translate-y-2/4">
                              {option.icon}
                            </span>
                            <span className="block text-sm font-light dark:text-black">
                              {option.name}
                            </span>
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        {paymentMethods
                          ?.filter(
                            (x: any) =>
                              matchStrings(
                                x?.payment_method_type,
                                PaymentMethodType.NETBANKING,
                                true
                              ) &&
                              matchStrings(
                                x?.payment_method,
                                `${preferredPaymentMethod?.key}_${preferredPaymentMethod?.type}`,
                                true
                              )
                          )
                          ?.map((option: any, idx: number) => (
                            <div
                              key={idx}
                              className="relative w-full py-2 pb-0 pl-0 pr-2"
                            >
                              <span
                                className="block text-sm font-light truncate dark:text-black"
                                title={option.description}
                              >
                                {option.description}
                              </span>
                            </div>
                          ))}
                      </>
                    )}
                  </>
                )}
            </>
          )}
        </a>
        <button
          type="button"
          className="w-4/6 flex items-center justify-center px-4 py-2 -mr-0.5 text-white bg-black border-2 border-black rounded-sm hover:bg-gray-900"
          onClick={async (ev: any) => {
            // Validate basket
            if (!localStorage?.getItem(LocalStorage.Key.userEncryptedDetails)) {
              setAlert({
                type: 'error',
                msg: Messages.Errors['GENERIC_ERROR'],
              })
            } else {
              sendUserInfoToMoengage(defaultShippingAddress)
              Cookies.set(
                LocalStorage.Key.IS_OFFLINE_STORE,
                `${isChannelStore ? true : false}`
              )
              setItem(
                LocalStorage.Key.IS_OFFLINE_STORE,
                isChannelStore ? true : false
              )
              const reValidateResult = await fetchBasketReValidate()
              if (isAllItemsInStock(reValidateResult)) {
                const messageLength: number =
                  reValidateData?.message?.split(',').length ?? 0

                if (messageLength == 0) {
                  // No error while basket validation
                  await initiateExpressCheckout()
                } else {
                  if (showBasketRevalidateError) {
                    setAlert({
                      type: 'error',
                      msg: Messages.Errors['BASKET_VALIDATION_FAILED'],
                    })
                  }
                }
              }
            }
          }}
        >
          <span className="flex justify-between w-full">
            <span className="block w-2/4 leading-none text-left">
              <span className="text-xs text-white opacity-90">Total</span>
              <span className="block text-sm text-left text-white">
                {priceFormat(cartItems.grandTotal?.raw?.withTax)}
              </span>
            </span>
            <span className="flex items-center content-center justify-end w-2/4 text-sm">
              <span className="pr-2 font-semibold font-14">Proceed</span>
              <i className="pl-2 sprite-icon arrow-right-white"></i>
            </span>
          </span>
        </button>
      </div>
    </div>
  )
}

export default BasketExpressCheckoutMainSection
